import React from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import "./MarkdownPreview.css";

type SearchResultCitation = { no: number; [k: string]: any };

type Props = {
  children:
    | string
    | (string | SearchResultCitation[])[]
    | (string | SearchResultCitation)[];
  onCitationClick?: any;
  /** Optional: supply when children is a plain string with [n] tokens */
  citations?: SearchResultCitation[];
};

const normalizeMarkdown = (raw: string) => {
  // Base & newline normalization
  let s = (raw ?? "").toString().replace(/\r\n?/g, "\n").trim();

  // Scrub problematic characters
  s = s
    .replace(/\uFFFD/g, "") // Unicode replacement char
    .replace(/[\u200B-\u200D\uFEFF]/g, ""); // zero-width chars

  // Strip accidental wrapping quotes/backticks
  s = s.replace(/^["'`]\s*/, "").replace(/\s*["'`]$/, "");

  // HTML → Markdown-friendly
  s = s
    .replace(/<br\s*\/?>/gi, "\n") // render <br> as newline
    .replace(/&nbsp;/gi, " "); // non-breaking space → space

  // Convert "•" bullets to Markdown "-"
  s = s.replace(/^\s*•\s+/gm, "- ");

  // Fix glued GFM tables
  s = s
    .replace(/\|\s*\|\s*(?=\|?-{2,})/g, "|\n|") // header|separator newline
    .replace(/\|\s*\|\s*\|/g, "|\n|"); // row newline

  return s;
};

const isCitation = (v: unknown): v is SearchResultCitation =>
  !!v &&
  typeof v === "object" &&
  "no" in (v as any) &&
  Number.isFinite((v as any).no);

const MarkdownPreview: React.FC<Props> = ({
  children,
  onCitationClick,
  citations,
}) => {
  // Case A: children is an array of strings + citation objects (best case)
  if (Array.isArray(children)) {
    return (
      <div className="prose prose-sm max-w-none">
        {children.map((part, i) => {
          if (typeof part === "string" || part instanceof String) {
            return (
              <Markdown
                key={`md-${i}`}
                remarkPlugins={[remarkGfm, remarkBreaks]}
                components={{ p: ({ node, ...props }) => <span {...props} /> }}
              >
                {normalizeMarkdown(String(part))}
              </Markdown>
            );
          }
          // if (isCitation(part)) {
          //   const n = part.no;
          //   return onCitationClick ? (
          //     <button
          //       key={`cit-${n}-${i}`}
          //       type="button"
          //       className="citation-link"
          //       onClick={() => onCitationClick(part)}
          //       aria-label={`Open citation ${n}`}
          //     >
          //       [{n}]
          //     </button>
          //   ) : (
          //     <span key={`cit-${n}-${i}`}>[{n}]</span>
          //   );
          // }
          // Unknown item: ignore
          return null;
        })}
      </div>
    );
  }

  // Case B: children is a single string containing [n] tokens
  const md = normalizeMarkdown(
    typeof children === "string"
      ? children
      : children instanceof String
      ? children.valueOf()
      : String(children ?? "")
  );

  // Split and render; if citations[] provided, map n -> object for onClick
  const parts = md.split(/(\[(\d+)\])/g); // keep [n] and capture n

  return (
    <div className="prose prose-sm max-w-none">
      {parts.map((part, i) => {
        // const m = part.match(/^\[(\d+)\]$/);
        // if (m) {
        //   const n = Number(m[1]);
        //   const obj = citations?.[n - 1]; // assumes numbering starts at 1
        //   return onCitationClick && obj ? (
        //     <button
        //       key={`cit-${n}-${i}`}
        //       type="button"
        //       className="text-blue-600 underline decoration-dotted cursor-pointer mx-0.5"
        //       onClick={() => onCitationClick(obj)}
        //       aria-label={`Open citation ${n}`}
        //     >
        //       [{n}]
        //     </button>
        //   ) : (
        //     <span key={`cit-${n}-${i}`}>[{n}]</span>
        //   );
        // }
        return (
          <Markdown
            key={`md-${i}`}
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={{ p: ({ node, ...props }) => <span {...props} /> }}
          >
            {part}
          </Markdown>
        );
      })}
    </div>
  );
};

export default MarkdownPreview;
